import { Canvas, type State } from '@multiplekex/shallot';
import { setupCanvasRecording } from '@blog-components/recording';
import { connectStepControls, type StepContent } from './ui';

export function setupCanvas(canvas: HTMLCanvasElement): void {
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * window.devicePixelRatio;
  canvas.height = rect.height * window.devicePixelRatio;
}

export function setupVisibilityControl(state: State): void {
  new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      state.start();
    } else {
      state.stop();
    }
  }).observe(document.documentElement);
}

export function createVisibilityLoop(
  state: State,
  canvas?: HTMLCanvasElement,
  onTick?: (dt: number) => void
): void {
  const canvasEl = canvas ?? Canvas.from(state)?.element;
  let visible = false;
  let lastTime = performance.now();

  function tick(now: number): void {
    if (!visible) return;
    const dt = (now - lastTime) / 1000;
    lastTime = now;
    // Skip stepping if canvas is paused (e.g., during recording)
    if (!canvasEl?.__paused__) {
      state.step(dt);
      onTick?.(dt);
    }
    requestAnimationFrame(tick);
  }

  new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      if (!visible) {
        visible = true;
        lastTime = performance.now();
        requestAnimationFrame(tick);
      }
    } else {
      visible = false;
    }
  }).observe(document.documentElement);
}

export function setupScrollPassthrough(canvas: HTMLCanvasElement): void {
  canvas.addEventListener(
    'wheel',
    (e) => {
      window.parent.postMessage({ type: 'iframe-scroll', deltaY: e.deltaY }, '*');
    },
    { passive: true }
  );
}

export function setupIframeScrollListener(): void {
  window.addEventListener('message', (e) => {
    if (e.data?.type === 'iframe-scroll' && typeof e.data.deltaY === 'number') {
      window.scrollBy(0, e.data.deltaY);
    }
  });
}

export function setupDemo(
  canvas: HTMLCanvasElement,
  state: State,
  steps?: StepContent[],
  onStepChange?: (step: number) => void
): () => void {
  canvas.__state__ = state;
  setupCanvasRecording(canvas, state);

  const disposeControls = steps
    ? connectStepControls(canvas, state, steps, onStepChange)
    : () => {};

  createVisibilityLoop(state, canvas);

  const reportHeight = () => {
    window.parent.postMessage({ type: 'iframe-resize', height: document.body.scrollHeight }, '*');
  };
  reportHeight();
  window.addEventListener('resize', reportHeight);

  return disposeControls;
}
